/**
 * 流年运势引擎 — 大运流年联动版 v2
 * 核心原则：流年评分必须同时纳入原局喜忌、当前大运干支、流年与原局/大运的刑冲合害、格局助破
 * 输出结构：年度主题 + 触发点 + 风险位 + 主动发力方向
 */

import {
  STEMS, BRANCHES,
  type Stem, type Branch, type Element, type FourPillars,
  STEM_ELEMENT, BRANCH_HIDDEN,
} from '../../baziCalc';
import { getShishen, ELEMENT_COLORS, type Shishen } from './shishenEngine';
import { calcDayStrength } from './xiyongEngine';
import { calcDayun, type DayunItem } from './dayunEngine';
import { judgeGeju, type GejuResult } from './gejuEngine';
import {
  ELEMENT_GENERATES,
  ELEMENT_OVERCOMES,
  ELEMENT_GENERATED_BY,
  ELEMENT_OVERCOME_BY,
} from '@/utils/calcTools/unifiedRules';

// ═══════════════════════════════════════════════════════════════
//  数据类型
// ═══════════════════════════════════════════════════════════════

export interface LiunianDomain {
  score: number;
  level: '优秀' | '良好' | '平稳' | '注意' | '谨慎';
  desc: string;
}

export interface ConflictItem {
  type: '冲' | '合' | '刑' | '害' | '破' | '三合' | '半合' | '天干合' | '天干冲';
  target: string;
  effect: '吉' | '凶' | '中性';
  desc: string;
}

export interface LiunianItem {
  year: number;
  stem: Stem;
  branch: Branch;
  stemEl: Element;
  branchEl: Element;
  stemColor: string;
  branchColor: string;
  stemShishen: Shishen;
  branchShishen: Shishen;

  // 大运联动
  dayunStem: Stem;
  dayunBranch: Branch;
  dayunShishen: Shishen;
  dayunEl: Element;
  dayunColor: string;

  // 刑冲合害
  conflicts: ConflictItem[];

  // 综合评分
  overallScore: number;
  overallLevel: '大吉' | '吉' | '平顺' | '需注意' | '挑战';

  // 新内容结构
  annualTheme: string;
  triggerPoint: string;
  riskArea: string;
  proactive: string;

  // 四域评分（辅助参考）
  career: LiunianDomain;
  wealth: LiunianDomain;
  love: LiunianDomain;
  health: LiunianDomain;

  keyAdvice: string[];
  auspicious: string[];
  resolve?: string;
  isCurrent: boolean;

  // 评分因子（debug / 展示用）
  factors: string[];
}

// ═══════════════════════════════════════════════════════════════
//  刑冲合害规则
// ═══════════════════════════════════════════════════════════════

const TIAN_GAN_HE: [Stem, Stem][] = [
  ['甲', '己'], ['乙', '庚'], ['丙', '辛'], ['丁', '壬'], ['戊', '癸'],
];

const TIAN_GAN_CHONG: [Stem, Stem][] = [
  ['甲', '庚'], ['乙', '辛'], ['丙', '壬'], ['丁', '癸'],
];

const DI_ZHI_CHONG: [Branch, Branch][] = [
  ['子', '午'], ['丑', '未'], ['寅', '申'], ['卯', '酉'], ['辰', '戌'], ['巳', '亥'],
];

const DI_ZHI_HAI: [Branch, Branch][] = [
  ['子', '未'], ['丑', '午'], ['寅', '巳'], ['卯', '辰'], ['申', '亥'], ['酉', '戌'],
];

const DI_ZHI_LIU_HE: [Branch, Branch][] = [
  ['子', '丑'], ['寅', '亥'], ['卯', '戌'], ['辰', '酉'], ['巳', '申'], ['午', '未'],
];

const DI_ZHI_SAN_HE: [Branch, Branch, Branch, Element][] = [
  ['申', '子', '辰', '水'],
  ['巳', '酉', '丑', '金'],
  ['寅', '午', '戌', '火'],
  ['亥', '卯', '未', '木'],
];

const DI_ZHI_BAN_HE: [Branch, Branch, Element][] = [
  ['申', '子', '水'], ['子', '辰', '水'],
  ['巳', '酉', '金'], ['酉', '丑', '金'],
  ['寅', '午', '火'], ['午', '戌', '火'],
  ['亥', '卯', '木'], ['卯', '未', '木'],
];

const DI_ZHI_SAN_XING: [Branch, Branch, Branch][] = [
  ['寅', '巳', '申'], ['丑', '戌', '未'],
];

const DI_ZHI_ZI_XING: Branch[] = ['辰', '午', '酉', '亥'];

const DI_ZHI_PO: [Branch, Branch][] = [
  ['子', '酉'], ['丑', '辰'], ['寅', '亥'], ['卯', '午'], ['巳', '申'], ['未', '戌'],
];

function hasPair(pairs: Array<[string, string]>, a: string, b: string): boolean {
  return pairs.some(([x, y]) => (x === a && y === b) || (x === b && y === a));
}

function isTianGanHe(a: Stem, b: Stem): boolean { return hasPair(TIAN_GAN_HE, a, b); }
function isTianGanChong(a: Stem, b: Stem): boolean { return hasPair(TIAN_GAN_CHONG, a, b); }
function isChong(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_CHONG, a, b); }
function isHe(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_LIU_HE, a, b); }
function isHai(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_HAI, a, b); }
function isPo(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_PO, a, b); }
function isZiXing(a: Branch, b: Branch): boolean { return a === b && DI_ZHI_ZI_XING.includes(a); }

function isSanHe(allBranches: Branch[]): { element: Element; branches: Branch[] } | null {
  for (const [x, y, z, el] of DI_ZHI_SAN_HE) {
    if (allBranches.includes(x) && allBranches.includes(y) && allBranches.includes(z)) {
      return { element: el, branches: [x, y, z] };
    }
  }
  return null;
}

function isBanHe(a: Branch, b: Branch): { element: Element } | null {
  for (const [x, y, el] of DI_ZHI_BAN_HE) {
    if ((x === a && y === b) || (x === b && y === a)) return { element: el };
  }
  return null;
}

function isSanXing(allBranches: Branch[]): boolean {
  return DI_ZHI_SAN_XING.some(([x, y, z]) => allBranches.includes(x) && allBranches.includes(y) && allBranches.includes(z));
}

// ═══════════════════════════════════════════════════════════════
//  辅助函数
// ═══════════════════════════════════════════════════════════════

function getBranchMainEl(branch: Branch): Element {
  const hidden = BRANCH_HIDDEN[branch];
  return hidden.length > 0 ? STEM_ELEMENT[hidden[0].stem] : '土';
}

function getBranchMainStem(branch: Branch): Stem {
  const hidden = BRANCH_HIDDEN[branch];
  return hidden.length > 0 ? hidden[0].stem : '己';
}

function getXiElements(dayStemEl: Element, bodyStrength: 'strong' | 'weak' | 'neutral'): Element[] {
  if (bodyStrength === 'strong') {
    return [ELEMENT_OVERCOME_BY[dayStemEl], ELEMENT_GENERATES[dayStemEl], ELEMENT_OVERCOMES[dayStemEl]];
  }
  if (bodyStrength === 'weak') {
    return [dayStemEl, ELEMENT_GENERATED_BY[dayStemEl]];
  }
  return [ELEMENT_GENERATES[dayStemEl], ELEMENT_OVERCOMES[dayStemEl]];
}

function getJiElements(dayStemEl: Element, bodyStrength: 'strong' | 'weak' | 'neutral'): Element[] {
  if (bodyStrength === 'strong') {
    return [dayStemEl, ELEMENT_GENERATED_BY[dayStemEl]];
  }
  if (bodyStrength === 'weak') {
    return [ELEMENT_OVERCOME_BY[dayStemEl], ELEMENT_GENERATES[dayStemEl], ELEMENT_OVERCOMES[dayStemEl]];
  }
  return [dayStemEl];
}

function scoreToDomainLevel(score: number): LiunianDomain['level'] {
  if (score >= 82) return '优秀';
  if (score >= 70) return '良好';
  if (score >= 58) return '平稳';
  if (score >= 45) return '注意';
  return '谨慎';
}

function scoreToOverallLevel(score: number): LiunianItem['overallLevel'] {
  if (score >= 80) return '大吉';
  if (score >= 68) return '吉';
  if (score >= 56) return '平顺';
  if (score >= 44) return '需注意';
  return '挑战';
}

// ═══════════════════════════════════════════════════════════════
//  核心评分引擎
// ═══════════════════════════════════════════════════════════════

function calcLiunianScore(
  year: number,
  fourPillars: FourPillars,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  dayunItem: DayunItem | undefined,
  geju: GejuResult,
): { score: number; factors: string[]; conflicts: ConflictItem[] } {
  let score = 60;
  const factors: string[] = [];
  const conflicts: ConflictItem[] = [];

  const stemIdx = ((year - 4) % 10 + 10) % 10;
  const branchIdx = ((year - 4) % 12 + 12) % 12;
  const liuStem = STEMS[stemIdx];
  const liuBranch = BRANCHES[branchIdx];
  const liuStemEl = STEM_ELEMENT[liuStem];
  const liuBranchEl = getBranchMainEl(liuBranch);
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];

  const xi = getXiElements(dayStemEl, bodyStrength);
  const ji = getJiElements(dayStemEl, bodyStrength);

  // ── 1. 身强身弱 + 流年十神匹配 ───────────────────────────
  const stemSS = getShishen(dayStem, liuStem);
  const branchMainStem = getBranchMainStem(liuBranch);
  const branchSS = branchMainStem ? getShishen(dayStem, branchMainStem) : stemSS;

  const strengthAdj = calcStrengthAdjust(stemSS, bodyStrength);
  score += strengthAdj;
  if (strengthAdj > 0) factors.push('流年十神契合日主喜忌');
  else if (strengthAdj < 0) factors.push('流年十神对日主形成压力');

  // ── 2. 喜用神匹配 ───────────────────────────────────────
  const liuEls = [liuStemEl, liuBranchEl];
  const isXi = liuEls.some(el => xi.includes(el));
  const isJi = liuEls.some(el => ji.includes(el));
  if (isXi) { score += 8; factors.push('流年五行逢命局喜用神'); }
  else if (isJi) { score -= 8; factors.push('流年五行逢命局忌神'); }

  // ── 3. 大运联动（核心）──────────────────────────────────
  if (dayunItem) {
    const dyStemEl = STEM_ELEMENT[dayunItem.stem];
    const dyBranchEl = getBranchMainEl(dayunItem.branch);
    const dyEls = [dyStemEl, dyBranchEl];
    const dyIsXi = dyEls.some(el => xi.includes(el));
    const dyIsJi = dyEls.some(el => ji.includes(el));

    if (dyIsXi) { score += 5; factors.push('大运逢喜用神，为流年提供底托'); }
    else if (dyIsJi) { score -= 5; factors.push('大运逢忌神，流年底色偏弱'); }

    // 大运与流年天干
    if (isTianGanHe(liuStem, dayunItem.stem)) {
      score += 4;
      conflicts.push({ type: '天干合', target: `大运天干${dayunItem.stem}`, effect: '吉', desc: '流年天干与大运天干相合，天地交泰，行事顺遂' });
    }
    if (isTianGanChong(liuStem, dayunItem.stem)) {
      score -= 5;
      conflicts.push({ type: '天干冲', target: `大运天干${dayunItem.stem}`, effect: '凶', desc: '流年天干冲大运天干，气机震荡，变动较多' });
    }

    // 大运与流年地支
    if (isChong(liuBranch, dayunItem.branch)) {
      score -= 8;
      conflicts.push({ type: '冲', target: `大运地支${dayunItem.branch}`, effect: '凶', desc: '流年冲大运，运势波动，事业感情易有突变' });
    }
    if (isHe(liuBranch, dayunItem.branch)) {
      score += 5;
      conflicts.push({ type: '合', target: `大运地支${dayunItem.branch}`, effect: '吉', desc: '流年合大运，运势和顺，人际合作顺畅' });
    }
    if (isHai(liuBranch, dayunItem.branch)) {
      score -= 3;
      conflicts.push({ type: '害', target: `大运地支${dayunItem.branch}`, effect: '凶', desc: '流年害大运，暗藏小人，合作需防口舌' });
    }
    if (isPo(liuBranch, dayunItem.branch)) {
      score -= 2;
      conflicts.push({ type: '破', target: `大运地支${dayunItem.branch}`, effect: '凶', desc: '流年破大运，财物流失，计划易受阻' });
    }
    if (isZiXing(liuBranch, dayunItem.branch)) {
      score -= 4;
      conflicts.push({ type: '刑', target: `大运地支${dayunItem.branch}`, effect: '凶', desc: '流年与大运自刑，内心纠结，决策易反复' });
    }
    const banhe = isBanHe(liuBranch, dayunItem.branch);
    if (banhe) {
      score += 3;
      conflicts.push({ type: '半合', target: `流年${liuBranch}合大运${dayunItem.branch}`, effect: '吉', desc: `流年与大运半合${banhe.element}局，运势渐入佳境` });
    }

    // 三合局（流年+大运+原局四支）
    const allBranches = [fourPillars.year.branch, fourPillars.month.branch, fourPillars.day.branch, fourPillars.hour.branch, dayunItem.branch, liuBranch];
    const sanhe = isSanHe(allBranches);
    if (sanhe) {
      score += 6;
      conflicts.push({ type: '三合', target: `${sanhe.branches.join('')}合${sanhe.element}局`, effect: '吉', desc: `流年与大运、原局构成${sanhe.branches.join('')}${sanhe.element}三合局，大势所趋` });
    }
    if (isSanXing(allBranches)) {
      score -= 6;
      conflicts.push({ type: '刑', target: '流年大运原局三刑', effect: '凶', desc: '流年、大运与原局构成三刑，是非纠缠，官非口舌需防' });
    }
  }

  // ── 4. 流年与原局四柱关系 ───────────────────────────────
  // 日柱（最重要，代表命主自身）
  if (isChong(liuBranch, fourPillars.day.branch)) {
    score -= 7;
    conflicts.push({ type: '冲', target: '日柱地支', effect: '凶', desc: '流年冲日柱，根基动摇，健康感情需格外注意' });
  }
  if (isHe(liuBranch, fourPillars.day.branch)) {
    score += 4;
    conflicts.push({ type: '合', target: '日柱地支', effect: '吉', desc: '流年合日柱，根基稳固，人缘桃花顺遂' });
  }
  if (isHai(liuBranch, fourPillars.day.branch)) {
    score -= 3;
    conflicts.push({ type: '害', target: '日柱地支', effect: '凶', desc: '流年害日柱，暗中有损，防小人背后' });
  }
  if (isTianGanHe(liuStem, fourPillars.day.stem)) {
    score += 3;
    conflicts.push({ type: '天干合', target: '日柱天干', effect: '吉', desc: '流年天干合日主，贵人暗助，办事顺遂' });
  }

  // 年柱
  if (isChong(liuBranch, fourPillars.year.branch)) {
    score -= 3;
    conflicts.push({ type: '冲', target: '年柱地支', effect: '凶', desc: '流年冲年柱，长辈、根基可能有变，注意家庭关系' });
  }
  if (isHe(liuBranch, fourPillars.year.branch)) {
    score += 2;
    conflicts.push({ type: '合', target: '年柱地支', effect: '吉', desc: '流年合年柱，根基稳固，长辈缘佳' });
  }

  // 月柱（事业/月令）
  if (isHe(liuBranch, fourPillars.month.branch)) {
    score += 2;
    conflicts.push({ type: '合', target: '月柱地支', effect: '吉', desc: '流年合月柱，事业方向有人提携，职场顺遂' });
  }
  if (isChong(liuBranch, fourPillars.month.branch)) {
    score -= 2;
    conflicts.push({ type: '冲', target: '月柱地支', effect: '凶', desc: '流年冲月柱，工作环境可能有变动' });
  }

  // 时柱（子女/晚年）
  if (isChong(liuBranch, fourPillars.hour.branch)) {
    score -= 2;
    conflicts.push({ type: '冲', target: '时柱地支', effect: '凶', desc: '流年冲时柱，子女、晚辈关系需注意' });
  }

  // ── 5. 格局影响 ─────────────────────────────────────────
  const helpsGeju = liuEls.some(el => geju.lucky.includes(el));
  const breaksGeju = liuEls.some(el => geju.unlucky.includes(el));
  if (helpsGeju) { score += 5; factors.push('流年五行助格局，格局得助'); }
  if (breaksGeju) { score -= 5; factors.push('流年五行破格，格局有损'); }

  // ── 6. 分数边界 ─────────────────────────────────────────
  score = Math.min(96, Math.max(12, Math.round(score)));

  return { score, factors, conflicts };
}

function calcStrengthAdjust(
  shishen: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
): number {
  if (bodyStrength === 'strong') {
    if (['正官', '七杀'].includes(shishen)) return +8;
    if (['食神', '伤官'].includes(shishen)) return +8;
    if (['正财', '偏财'].includes(shishen)) return +5;
    if (['比肩', '劫财'].includes(shishen)) return -8;
    if (['正印', '偏印'].includes(shishen)) return -5;
  } else if (bodyStrength === 'weak') {
    if (['比肩', '劫财'].includes(shishen)) return +8;
    if (['正印', '偏印'].includes(shishen)) return +8;
    if (['正官', '七杀'].includes(shishen)) return -8;
    if (['食神', '伤官'].includes(shishen)) return -5;
    if (['正财', '偏财'].includes(shishen)) return -5;
  }
  return 0;
}

// ═══════════════════════════════════════════════════════════════
//  四域评分（基于联动逻辑，非模板套话）
// ═══════════════════════════════════════════════════════════════

function calcDomainScores(
  stemShishen: Shishen,
  branchShishen: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: ConflictItem[],
  dayunItem: DayunItem | undefined,
  xiyong: { xi: Element[]; ji: Element[] },
  liuStemEl: Element,
  liuBranchEl: Element,
): Record<'career' | 'wealth' | 'love' | 'health', { score: number; desc: string }> {
  const base = { career: 60, wealth: 60, love: 60, health: 60 };
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');
  const hasHe = conflicts.some(c => c.type === '合');
  const dySS = dayunItem?.stemShishen;

  // 事业
  if (['正官', '七杀'].includes(stemShishen)) {
    base.career += 8;
    if (dySS && ['正官', '七杀'].includes(dySS)) base.career += 5;
  }
  if (['食神', '伤官'].includes(stemShishen)) {
    base.career += 5;
    if (dySS && ['食神', '伤官'].includes(dySS)) base.career += 4;
  }
  if (hasChong) base.career -= 6;
  if (hasHe) base.career += 4;
  if (bodyStrength === 'weak' && ['正官', '七杀'].includes(stemShishen)) base.career -= 5;

  // 财运
  if (['正财', '偏财'].includes(stemShishen)) {
    base.wealth += 8;
    if (dySS && ['正财', '偏财'].includes(dySS)) base.wealth += 5;
  }
  if (['食神', '伤官'].includes(stemShishen)) base.wealth += 4; // 食伤生财
  if (hasChong) base.wealth -= 5;
  if (conflicts.some(c => c.type === '破')) base.wealth -= 4;
  if (bodyStrength === 'weak' && ['正财', '偏财'].includes(stemShishen)) base.wealth -= 4;

  // 感情
  if (['正官', '正财', '偏财'].includes(stemShishen)) {
    base.love += 5;
    if (hasHe) base.love += 5;
  }
  if (['七杀', '伤官', '劫财'].includes(stemShishen)) {
    base.love -= 4;
    if (hasChong) base.love -= 4;
  }
  if (conflicts.some(c => c.target.includes('日柱') && c.type === '冲')) base.love -= 5;
  if (conflicts.some(c => c.target.includes('日柱') && c.type === '合')) base.love += 5;

  // 健康
  if (hasChong && conflicts.some(c => c.target.includes('日柱'))) base.health -= 6;
  if (hasXing) base.health -= 4;
  if (bodyStrength === 'weak') {
    if (['正官', '七杀', '食神', '伤官', '正财', '偏财'].includes(stemShishen)) base.health -= 3;
  }
  if (['正印', '比肩'].includes(stemShishen)) base.health += 3;

  // 喜忌调整
  const isXi = xiyong.xi.includes(liuStemEl) || xiyong.xi.includes(liuBranchEl);
  const isJi = xiyong.ji.includes(liuStemEl) || xiyong.ji.includes(liuBranchEl);
  if (isXi) {
    base.career += 3; base.wealth += 3; base.love += 3; base.health += 3;
  }
  if (isJi) {
    base.career -= 3; base.wealth -= 3; base.love -= 3; base.health -= 3;
  }

  return {
    career: {
      score: Math.min(96, Math.max(15, Math.round(base.career))),
      desc: buildCareerDesc(stemShishen, branchShishen, bodyStrength, conflicts, dayunItem),
    },
    wealth: {
      score: Math.min(96, Math.max(15, Math.round(base.wealth))),
      desc: buildWealthDesc(stemShishen, branchShishen, bodyStrength, conflicts, dayunItem),
    },
    love: {
      score: Math.min(96, Math.max(15, Math.round(base.love))),
      desc: buildLoveDesc(stemShishen, branchShishen, bodyStrength, conflicts, dayunItem),
    },
    health: {
      score: Math.min(96, Math.max(15, Math.round(base.health))),
      desc: buildHealthDesc(stemShishen, branchShishen, bodyStrength, conflicts, dayunItem, liuStemEl),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
//  四域描述生成（简短、联动、非模板）
// ═══════════════════════════════════════════════════════════════

function buildCareerDesc(
  stemSS: Shishen,
  branchSS: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: ConflictItem[],
  dayunItem: DayunItem | undefined,
): string {
  const parts: string[] = [];
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasHe = conflicts.some(c => c.type === '合' || c.type === '天干合');
  const dySS = dayunItem?.stemShishen;

  // 流年十神主线
  if (['正官', '七杀'].includes(stemSS)) {
    parts.push('事业方面权力与责任并存');
    if (bodyStrength === 'strong') parts.push('身强可担，适合争取更大的管理舞台');
    else parts.push('身弱压力偏大，宜稳扎稳打，不求冒进');
  } else if (['食神', '伤官'].includes(stemSS)) {
    parts.push('创意与才华是此年职场核心竞争力');
    if (dySS && ['食神', '伤官'].includes(dySS)) {
      parts.push('大运也走食伤运，才华变现窗口大开，是展示自我的黄金期');
    } else if (dySS && ['正官', '七杀'].includes(dySS)) {
      parts.push('大运官杀压制食伤，才华发挥受到体制约束，需在规则内寻找突破口');
    }
  } else if (['正财', '偏财'].includes(stemSS)) {
    parts.push('财富驱动事业，业绩导向明显');
    if (hasHe) parts.push('合作生财，宜借助团队力量');
    if (dySS && ['正财', '偏财'].includes(dySS)) parts.push('大运财运同步旺盛，事业财务双线推进');
  } else if (['正印', '偏印'].includes(stemSS)) {
    parts.push('学习进修、资质积累是此年事业主轴');
    if (dySS && ['正官', '七杀'].includes(dySS)) parts.push('大运官杀生印，上级赏识与晋升机会并存');
    else if (dySS && ['比肩', '劫财'].includes(dySS)) parts.push('大运比劫竞争激烈，需以专业能力突围');
  } else if (['比肩', '劫财'].includes(stemSS)) {
    parts.push('同行竞争激烈，需强化差异化优势');
    if (dySS && ['食神', '伤官'].includes(dySS)) parts.push('大运食伤泄秀，竞争中可凭才华脱颖而出');
    else if (dySS && ['正官', '七杀'].includes(dySS)) parts.push('大运官杀制劫，竞争压力被外部规则约束，反而有序');
  } else {
    parts.push('事业节奏平稳，适合深耕已有领域');
  }

  // 大运底色补充（未在上面覆盖的情况）
  if (dySS && !parts.some(p => p.includes('大运'))) {
    if (['正官', '七杀'].includes(dySS)) parts.push('大运官杀运底色，职场规则与权责意识贯穿全年');
    else if (['食神', '伤官'].includes(dySS)) parts.push('大运食伤运底色，创造力与表达欲旺盛，适合开拓新方向');
    else if (['正财', '偏财'].includes(dySS)) parts.push('大运财运底色稳健，事业与财务联动紧密');
    else if (['正印', '偏印'].includes(dySS)) parts.push('大运印运守护，贵人庇护明显，适合借助平台资源');
    else if (['比肩', '劫财'].includes(dySS)) parts.push('大运比劫运，竞争意识强，需防内耗与合作摩擦');
  }

  if (hasChong) parts.push('流年冲克带来变动，跳槽或转型需慎重评估');
  if (hasHe && !hasChong) parts.push('人际和合，贵人提携，推进项目事半功倍');

  return parts.join('；') + '。';
}

function buildWealthDesc(
  stemSS: Shishen,
  branchSS: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: ConflictItem[],
  dayunItem: DayunItem | undefined,
): string {
  const parts: string[] = [];
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasPo = conflicts.some(c => c.type === '破');
  const hasHai = conflicts.some(c => c.type === '害');
  const dySS = dayunItem?.stemShishen;

  // 流年十神主线
  if (['正财', '偏财'].includes(stemSS)) {
    parts.push('财运活跃，进财机会较多');
    if (bodyStrength === 'strong') parts.push('身强可担财，适合稳健投资和资产积累');
    else parts.push('身弱财重，财来财去，建议以守为主');
    if (dySS && ['食神', '伤官'].includes(dySS)) parts.push('大运食伤生财，主动创收能力强，副业拓展有利');
    else if (dySS && ['比肩', '劫财'].includes(dySS)) parts.push('大运比劫分财，需防合伙纠纷与财务外漏');
    else if (dySS && ['正财', '偏财'].includes(dySS)) parts.push('大运财运同步旺盛，财富积累进入快车道');
  } else if (['食神', '伤官'].includes(stemSS)) {
    parts.push('才华变现路径通畅，技能与创意可直接转化为收入');
    if (dySS && ['正财', '偏财'].includes(dySS)) parts.push('大运财运配合，食伤生财效果显著，收入增长可期');
    else if (dySS && ['正官', '七杀'].includes(dySS)) parts.push('大运官杀克制食伤，才华变现受阻，需另辟蹊径');
  } else if (['比肩', '劫财'].includes(stemSS)) {
    parts.push('竞争环境激烈，需防财务纠纷与合伙风险');
    if (hasChong) parts.push('冲克之年，切忌借贷担保');
    if (dySS && ['正财', '偏财'].includes(dySS)) parts.push('大运财运底色尚可，但流年比劫分财，需严格管控支出');
  } else if (['正官', '七杀'].includes(stemSS)) {
    parts.push('财运与职位挂钩，薪资晋升是主要进财渠道');
    if (dySS && ['正财', '偏财'].includes(dySS)) parts.push('大运财运旺，官杀年薪资待遇有望提升');
  } else if (['正印', '偏印'].includes(stemSS)) {
    parts.push('印运耗财，此年宜守不宜攻，避免大额投资');
    if (dySS && ['正财', '偏财'].includes(dySS)) parts.push('大运财运底托，印年耗财有限，整体尚可');
  } else {
    parts.push('财运平稳，按部就班积累即可');
  }

  if (hasPo) parts.push('相破之年，注意财物损耗，守住已有积累');
  if (hasHai) parts.push('暗害之年，财务往来留凭证，防止隐性损失');

  return parts.join('；') + '。';
}

function buildLoveDesc(
  stemSS: Shishen,
  branchSS: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: ConflictItem[],
  dayunItem: DayunItem | undefined,
): string {
  const parts: string[] = [];
  const riChong = conflicts.some(c => c.target.includes('日柱') && c.type === '冲');
  const riHe = conflicts.some(c => c.target.includes('日柱') && c.type === '合');
  const hasHai = conflicts.some(c => c.type === '害');
  const dySS = dayunItem?.stemShishen;

  // 流年十神主线
  if (['正官', '正财', '偏财'].includes(stemSS)) {
    parts.push('感情趋向稳定，关系中有承诺和责任的能量');
    if (riHe) parts.push('流年合日柱，正缘机遇或感情升温');
    // 大运联动
    if (dySS && ['正官', '正财', '偏财'].includes(dySS)) {
      parts.push('大运同步走感情有利之神，婚恋运势持续向好，是推进关系的好时机');
    } else if (dySS && ['七杀', '伤官', '劫财'].includes(dySS)) {
      parts.push('大运底色有波动，感情虽有机遇但需防外力干扰，稳固关系需主动经营');
    } else if (dySS && ['正印', '偏印'].includes(dySS)) {
      parts.push('大运印运内敛，感情上偏向精神共鸣，适合深化情感连接而非快速推进');
    }
  } else if (['七杀', '伤官', '劫财'].includes(stemSS)) {
    parts.push('感情波动较大，易有争执或外缘干扰');
    if (riChong) parts.push('冲日柱之年，感情根基受考验，需主动维护');
    // 大运联动
    if (dySS && ['七杀', '伤官', '劫财'].includes(dySS)) {
      parts.push('大运底色同样激进，感情摩擦风险叠加，需格外注意沟通方式');
    } else if (dySS && ['正官', '正财', '偏财'].includes(dySS)) {
      parts.push('大运感情底色稳健，流年波动有大运托底，整体仍可控');
    } else if (dySS && ['正印', '偏印'].includes(dySS)) {
      parts.push('大运印运有一定缓冲，情绪管理得当可化解流年感情压力');
    }
  } else if (['比肩', '劫财'].includes(stemSS)) {
    parts.push('感情上竞争意识较强，需防第三者介入或感情中的控制欲');
    if (dySS && ['正官', '正财', '偏财'].includes(dySS)) {
      parts.push('大运感情底色稳定，比劫年的竞争感有大运压制，整体尚可');
    }
  } else if (['正印', '偏印'].includes(stemSS)) {
    parts.push('感情上偏向精神层面，实际行动力不足，需主动表达心意');
    if (dySS && ['正官', '正财', '偏财'].includes(dySS)) {
      parts.push('大运感情底色有利，印年虽内敛但大运提供外部机遇，可借势推进');
    }
  } else {
    parts.push('感情节奏平稳，宜在日常相处中增进默契');
    if (dySS && ['正官', '正财', '偏财'].includes(dySS)) {
      parts.push('大运感情底色向好，平稳流年中有大运助力，感情可稳步深化');
    } else if (dySS && ['七杀', '伤官', '劫财'].includes(dySS)) {
      parts.push('大运底色有波动，平稳流年中需防大运带来的感情暗流');
    }
  }

  if (riHe && !riChong && !parts.some(p => p.includes('桃花'))) parts.push('人缘桃花顺遂，单身者可积极社交');
  if (hasHai) parts.push('暗害之年，感情中防小人拨弄是非，减少不必要的感情纠纷');

  return parts.join('；') + '。';
}

function buildHealthDesc(
  stemSS: Shishen,
  branchSS: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: ConflictItem[],
  dayunItem: DayunItem | undefined,
  liuStemEl: Element,
): string {
  const parts: string[] = [];
  const riChong = conflicts.some(c => c.target.includes('日柱') && c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');
  const dySS = dayunItem?.stemShishen;
  const dyEl = dayunItem ? STEM_ELEMENT[dayunItem.stem] : null;

  const organMap: Record<Element, string> = {
    木: '肝胆', 火: '心与小肠', 土: '脾胃', 金: '肺与大肠', 水: '肾与膀胱',
  };
  parts.push(`流年${liuStemEl}气主导，注意${organMap[liuStemEl]}方面的养护`);

  // 大运五行对健康的叠加影响
  if (dyEl && dyEl !== liuStemEl) {
    parts.push(`大运走${dyEl}气，${organMap[dyEl]}同样需要关注，双重五行叠加，养生需兼顾两个系统`);
  } else if (dyEl && dyEl === liuStemEl) {
    parts.push(`大运与流年同走${dyEl}气，${organMap[dyEl]}压力集中，需重点养护`);
  }

  // 大运十神对健康的影响
  if (dySS) {
    if (['七杀', '正官'].includes(dySS) && bodyStrength === 'weak') {
      parts.push('大运官杀压身，身弱者精神压力大，需防焦虑与免疫力下降');
    } else if (['食神', '伤官'].includes(dySS) && bodyStrength === 'weak') {
      parts.push('大运食伤泄气，身弱者体力消耗偏大，注意补充营养与休息');
    } else if (['正印', '偏印'].includes(dySS)) {
      parts.push('大运印运守护，整体健康底色较好，但需防过度依赖静养而缺乏运动');
    } else if (['比肩', '劫财'].includes(dySS) && bodyStrength === 'strong') {
      parts.push('大运比劫身强，精力充沛，但需防过度消耗与运动损伤');
    }
  }

  if (riChong) parts.push('流年冲日柱，身体根基受扰，注意休息与情绪管理');
  if (hasXing) parts.push('刑克之年，气血运行可能不畅，建议规律运动和体检');
  if (bodyStrength === 'weak' && ['正官', '七杀', '食神', '伤官', '正财', '偏财'].includes(stemSS)) {
    parts.push('身弱逢克泄，精力消耗大，防止过劳');
  }

  return parts.join('；') + '。';
}

// ═══════════════════════════════════════════════════════════════
//  年度主题 / 触发点 / 风险位 / 主动发力方向 生成
// ═══════════════════════════════════════════════════════════════

function buildAnnualTheme(
  year: number,
  stemSS: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: ConflictItem[],
  dayunItem: DayunItem | undefined,
): { theme: string; trigger: string; risk: string; proactive: string } {
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');
  const hasHe = conflicts.some(c => c.type === '合' || c.type === '天干合');
  const hasSanHe = conflicts.some(c => c.type === '三合');
  const dySS = dayunItem?.stemShishen;

  // 年度主题
  let theme = '';
  if (hasChong || hasXing) {
    theme = `${year}年是变动与调整之年，外界环境推动您走出舒适区，在变局中寻新方向。这不是最轻松的一年，但往往是成长最多的一年——只要提前做好风险管理，压力完全可以转成成长机会`;
  } else if (hasHe || hasSanHe) {
    theme = `${year}年是和合与机遇之年，天时地利人和齐聚，适合推进重要计划。这是难得的顺风年，务必把握机遇积极行动`;
  } else if (['正官', '正印'].includes(stemSS)) {
    theme = `${year}年是积累与进阶之年，稳健推进，厚积薄发。规律、踏实、诚信，是这一年最好的策略`;
  } else if (['七杀', '伤官'].includes(stemSS)) {
    theme = `${year}年是挑战与突破之年，逆境激发潜能，主动求变方可破局。七杀有制则为权，压力下往往能展现超出预期的实力`;
  } else if (['食神', '偏财'].includes(stemSS)) {
    theme = `${year}年是才华与机遇之年，创造力旺盛，适合展示自我。天赋在这一年最容易被看见，主动出击即可`;
  } else if (['正财', '比肩'].includes(stemSS)) {
    theme = `${year}年是务实与协作之年，踏实积累，合作共赢。每一步扎实的努力都在为未来蓄力`;
  } else {
    theme = `${year}年是平稳发展之年，守成固本，静待时机。在稳定中打磨内功，蓄势待发`;
  }

  if (dySS) {
    if (['七杀', '伤官', '劫财'].includes(dySS)) {
      theme += '，大运势能带来深层变革，需以变应变，灵活应对';
    } else if (['正官', '正印', '食神', '正财', '偏财'].includes(dySS)) {
      theme += '，大运底托稳健，助力明显，相信自己的积累';
    }
  }

  // 触发点
  let trigger = '';
  const goodConflicts = conflicts.filter(c => c.effect === '吉');
  if (goodConflicts.some(c => c.type === '合')) {
    trigger = '人际合作、贵人引荐、感情升温是此年关键触发点，留意身边合作机会。每一次真诚的连接都可能带来意想不到的转机';
  } else if (goodConflicts.some(c => c.type === '三合')) {
    trigger = '大局成形、资源整合是核心触发点，适合启动需多方配合的大项目。三合成局之年，行动比等待更有价值';
  } else if (['正官', '正印'].includes(stemSS)) {
    trigger = '上级赏识、资质认证、规则认可将是您此年的关键触发点，主动争取评优与晋升机会';
  } else if (['食神', '伤官'].includes(stemSS)) {
    trigger = '才华展示、创意落地、作品发布将带来突破性机遇，把握每一个展示自我的窗口';
  } else if (['正财', '偏财'].includes(stemSS)) {
    trigger = '财务规划、投资机会、副业拓展是此年的财富触发点，提前布局比被动等待更有效';
  } else {
    trigger = '上半年（立春前后）和下半年（立秋前后）是两个关键时间节点，适合布局与调整方向';
  }

  // 风险位（调适建议结构）
  let risk = '';
  const badConflicts = conflicts.filter(c => c.effect === '凶');
  if (badConflicts.some(c => c.type === '冲')) {
    risk = `${year}年流年冲克，整体有变动，这不是"坏运"，而是"提醒您这一阶段更适合稳住节奏"。
【命理原因】流年地支与原局或大运形成六冲，气机震荡，根基受扰。
【现实表现】容易遭遇突发变动，情绪起伏较大，健康、感情、合同关系需格外留意。
【调和方法】
① 减少冲动决策，重要沟通留缓冲期；
② 规律作息，避免情绪积压；
③ 重大事项分阶段推进，合同、健康、关系都留检查点；
④ 关系中避免硬碰硬。
【鼓励收束】只要提前做好风险管理，冲克之年反而能在压力中发现平时忽视的机会。`;
  } else if (badConflicts.some(c => c.type === '刑')) {
    risk = `${year}年三刑是非，法律合同、口舌纷争、情绪管理是重点风险。
【现实表现】容易遇到口舌纠纷或合作矛盾，关系中硬碰硬容易两败俱伤。
【调和方法】
① 遇事多忍让少争执，凡事留痕；
② 合同文书仔细审核，不签口头协议；
③ 在关系中主动表达善意，给对方留台阶。
【鼓励收束】是非之年也是磨砺圆融之年，学会以柔化刚，人际格局会有质的提升。`;
  } else if (badConflicts.some(c => c.type === '害')) {
    risk = `${year}年暗害，人际需留意小人，财务往来要留凭证。
【调和方法】
① 不轻信他人口头承诺；
② 财务往来书面确认；
③ 减少不必要的应酬，多亲近贵人。
【鼓励收束】有觉察地去感知身边能量，不是多疑，而是更有边界感。此年过后识人辨事能力将显著提升。`;
  } else if (badConflicts.some(c => c.type === '破')) {
    risk = `${year}年有耗散之象，注意财物和精力的管理，避免无谓消耗。
【调和方法】定期审视资源分配，收紧非必要开支，聚焦核心目标。`;
  } else if (['七杀', '劫财'].includes(stemSS)) {
    risk = `${year}年压力与竞争并存，健康与财务是重点保护位。
【调和方法】
① 不宜冒进投资和过激行为；
② 规律运动以疏导压力；
③ 建立流程与边界，以规矩制衡冲动；
④ 健康如有不适，以专业医疗建议为准，命理分析仅为辅助参考。
【鼓励收束】七杀有制则为权，这一年正是锻造核心竞争力的机会，主动设定目标，把压力化为动力。`;
  } else if (bodyStrength === 'weak' && ['正官', '七杀', '食神', '伤官', '正财', '偏财'].includes(stemSS)) {
    risk = `${year}年身弱逢克泄，精力与健康是首要风险位。
【调和方法】
① 劳逸结合，量力而行；
② 减少高耗能任务；
③ 增加休息时段；
④ 健康如有不适，以专业医疗建议为准。
【鼓励收束】稳扎稳打同样能积累出色成果，这一年用质取代量，精力会更有效率。`;
  } else {
    risk = `${year}年整体平稳，但需防过度乐观。保持警觉，定期复盘年度目标，居安思危才能走得更远。`;
  }

  // 主动发力方向
  let proactive = '';
  if (bodyStrength === 'strong') {
    if (['正官', '七杀'].includes(stemSS)) {
      proactive = '适合主动争取晋升、扩大管理权限、承担更大责任，将压力转化为动力';
    } else if (['食神', '伤官'].includes(stemSS)) {
      proactive = '适合主动展示才华、推出新作品、开拓个人品牌，让创造力真正变现';
    } else if (['正财', '偏财'].includes(stemSS)) {
      proactive = '适合主动理财规划、拓展收入渠道、参与商业合作，把握财富增值窗口';
    } else {
      proactive = '适合主动学习充电、考证进修、扩大人脉，为未来蓄力积能';
    }
  } else if (bodyStrength === 'weak') {
    if (['正印', '偏印', '比肩', '劫财'].includes(stemSS)) {
      proactive = '适合主动寻求师长提携、结交盟友、借助外力壮大自身，借力之智不亚于独力之强';
    } else {
      proactive = '适合主动固本培元、精简项目、聚焦核心竞争力，少做多精，先把根扎深';
    }
  } else {
    proactive = '适合主动平衡各方面关系，稳健推进既有计划，不急不躁，旺时进取、衰时守成';
  }

  return { theme, trigger, risk, proactive };
}

// ═══════════════════════════════════════════════════════════════
//  建议 / 调和 / 吉祥信息
// ═══════════════════════════════════════════════════════════════

function buildKeyAdvice(
  stemSS: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  overallLevel: LiunianItem['overallLevel'],
  conflicts: ConflictItem[],
  dayunItem: DayunItem | undefined,
): string[] {
  const advice: string[] = [];
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');

  if (overallLevel === '大吉' || overallLevel === '吉') {
    advice.push('把握流年吉运窗口，主动推进核心计划，不宜拖延观望');
    if (['食神', '伤官'].includes(stemSS)) advice.push('才华变现的黄金期，大胆展示个人能力与创意成果');
    if (['正财', '偏财'].includes(stemSS)) advice.push('财务机遇多，适合稳健投资与资产配置');
  } else if (overallLevel === '需注意' || overallLevel === '挑战') {
    advice.push('流年有波动，重大决策前多方评估，预留弹性空间');
    if (hasChong) advice.push('冲克之年，健康与感情需格外维护，避免冲动行事');
    if (hasXing) advice.push('是非之年，合同文书仔细审核，减少口头承诺');
    if (bodyStrength === 'weak') advice.push('身弱逢流年压力，优先固本培元，不宜透支精力');
  } else {
    advice.push('运势平稳，脚踏实地积累，等待更有利的流年窗口');
  }

  if (dayunItem) {
    if (['正官', '正印', '食神', '正财'].includes(dayunItem.stemShishen)) {
      advice.push('大运底托稳健，流年虽有波动但根基仍在，顺势而为即可');
    } else if (['七杀', '伤官', '劫财'].includes(dayunItem.stemShishen)) {
      advice.push('大运势能激进，流年更需谨慎，避免与大势正面对抗');
    }
  }

  if (advice.length < 3) advice.push('定期复盘年度目标，及时调整策略方向');

  return advice.slice(0, 3);
}

function buildAuspicious(stemEl: Element, branchEl: Element): string[] {
  const colorMap: Record<Element, string> = {
    木: '绿色、青色', 火: '红色、橙色', 土: '黄色、米色',
    金: '白色、金色', 水: '蓝色、黑色',
  };
  const dirMap: Record<Element, string> = {
    木: '东方、东南方', 火: '南方', 土: '中央、西南方',
    金: '西方、西北方', 水: '北方',
  };
  return [
    `幸运颜色：${colorMap[stemEl]}`,
    `吉利方位：${dirMap[stemEl]}`,
    `流年能量：天干${stemEl}气 · 地支${branchEl}气交汇`,
  ];
}

function buildResolve(
  overallLevel: LiunianItem['overallLevel'],
  conflicts: ConflictItem[],
): string | undefined {
  if (overallLevel !== '需注意' && overallLevel !== '挑战') return undefined;

  const hasChong = conflicts.some(c => c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');
  const hasHai = conflicts.some(c => c.type === '害');

  if (hasChong) {
    return '调和建议：冲克之年宜静不宜动，重大决策延至下半年；佩戴五行属土的饰品（黄水晶、黄玉）以土为通关，调和冲突；多行善事积累阴德，以柔克刚。';
  }
  if (hasXing) {
    return '调和建议：三刑之年是非多，凡事留痕，合同协议务必书面确认；避免卷入他人纠纷；可佩戴五行属金的饰品以金泄刑气，保持低调内敛。';
  }
  if (hasHai) {
    return '调和建议：暗害之年防小人，财务往来留凭证，不轻易担保借贷；多亲近贵人，减少不必要的社交应酬；可佩戴五行属火的饰品以火生旺自身。';
  }
  return '调和建议：保持平和心态，行善积德；遇事多思后行，凡事三思而后动；运势是顺势而为的参考，积极心态是最好的生活态度。';
}

// ═══════════════════════════════════════════════════════════════
//  公开接口
// ═══════════════════════════════════════════════════════════════

export function calcLiunianRange(
  fourPillars: FourPillars,
  birthYear: number,
  birthMonth: number,
  birthDay: number,
  gender: 'male' | 'female',
  currentYear: number,
  range: { past: number; future: number } = { past: 1, future: 9 },
): LiunianItem[] {
  const bodyStrength = calcDayStrength(fourPillars);
  const geju = judgeGeju(fourPillars);
  const dayun = calcDayun(fourPillars, birthYear, birthMonth, birthDay, gender, currentYear);

  const dayStemEl = STEM_ELEMENT[fourPillars.day.stem];
  const xi = getXiElements(dayStemEl, bodyStrength);
  const ji = getJiElements(dayStemEl, bodyStrength);
  const xiyong = { xi, ji };

  const result: LiunianItem[] = [];
  for (let y = currentYear - range.past; y <= currentYear + range.future; y++) {
    if (y < birthYear) continue;

    const stemIdx = ((y - 4) % 10 + 10) % 10;
    const branchIdx = ((y - 4) % 12 + 12) % 12;
    const stem = STEMS[stemIdx];
    const branch = BRANCHES[branchIdx];
    const stemEl = STEM_ELEMENT[stem];
    const branchEl = getBranchMainEl(branch);
    const dayStem = fourPillars.day.stem;

    const stemShishen = getShishen(dayStem, stem);
    const branchMainStem = getBranchMainStem(branch);
    const branchShishen = branchMainStem ? getShishen(dayStem, branchMainStem) : stemShishen;

    // 当前年份所在大运
    const age = y - birthYear;
    const dayunItem = dayun.items.find(it => age >= it.startAge && age <= it.endAge);

    const { score, factors, conflicts } = calcLiunianScore(y, fourPillars, bodyStrength, dayunItem, geju);
    const overallScore = score;
    const overallLevel = scoreToOverallLevel(overallScore);

    const domains = calcDomainScores(stemShishen, branchShishen, bodyStrength, conflicts, dayunItem, xiyong, stemEl, branchEl);

    const { theme, trigger, risk, proactive } = buildAnnualTheme(y, stemShishen, bodyStrength, conflicts, dayunItem);

    const keyAdvice = buildKeyAdvice(stemShishen, bodyStrength, overallLevel, conflicts, dayunItem);
    const auspicious = buildAuspicious(stemEl, branchEl);
    const resolve = buildResolve(overallLevel, conflicts);

    result.push({
      year: y,
      stem,
      branch,
      stemEl,
      branchEl,
      stemColor: ELEMENT_COLORS[stemEl],
      branchColor: ELEMENT_COLORS[branchEl],
      stemShishen,
      branchShishen,
      dayunStem: dayunItem?.stem ?? dayun.items[0]?.stem ?? stem,
      dayunBranch: dayunItem?.branch ?? dayun.items[0]?.branch ?? branch,
      dayunShishen: dayunItem?.stemShishen ?? '比肩',
      dayunEl: dayunItem ? STEM_ELEMENT[dayunItem.stem] : stemEl,
      dayunColor: dayunItem ? ELEMENT_COLORS[STEM_ELEMENT[dayunItem.stem]] : ELEMENT_COLORS[stemEl],
      conflicts,
      overallScore,
      overallLevel,
      annualTheme: theme,
      triggerPoint: trigger,
      riskArea: risk,
      proactive,
      career: { score: domains.career.score, level: scoreToDomainLevel(domains.career.score), desc: domains.career.desc },
      wealth: { score: domains.wealth.score, level: scoreToDomainLevel(domains.wealth.score), desc: domains.wealth.desc },
      love: { score: domains.love.score, level: scoreToDomainLevel(domains.love.score), desc: domains.love.desc },
      health: { score: domains.health.score, level: scoreToDomainLevel(domains.health.score), desc: domains.health.desc },
      keyAdvice,
      auspicious,
      resolve,
      isCurrent: y === currentYear,
      factors,
    });
  }

  return result;
}

/** 构建默认流年兜底项（当出生年晚于当前年时使用） */
function buildFallbackLiunian(year: number, fourPillars: FourPillars): LiunianItem {
  const stemIdx = ((year - 4) % 10 + 10) % 10;
  const branchIdx = ((year - 4) % 12 + 12) % 12;
  const stem = STEMS[stemIdx];
  const branch = BRANCHES[branchIdx];
  const stemEl = STEM_ELEMENT[stem];
  const branchEl = getBranchMainEl(branch);
  const dayStem = fourPillars.day.stem;
  const stemShishen = getShishen(dayStem, stem);
  const branchMainStem = getBranchMainStem(branch);
  const branchShishen = branchMainStem ? getShishen(dayStem, branchMainStem) : stemShishen;
  const domainDefault: LiunianDomain = { score: 60, level: '平稳', desc: '平稳发展。' };
  return {
    year,
    stem,
    branch,
    stemEl,
    branchEl,
    stemColor: ELEMENT_COLORS[stemEl],
    branchColor: ELEMENT_COLORS[branchEl],
    stemShishen,
    branchShishen,
    dayunStem: stem,
    dayunBranch: branch,
    dayunShishen: stemShishen,
    dayunEl: stemEl,
    dayunColor: ELEMENT_COLORS[stemEl],
    conflicts: [],
    overallScore: 60,
    overallLevel: '平顺',
    annualTheme: `${year}年平稳发展之年，守成固本，静待时机。`,
    triggerPoint: '保持稳定节奏，积累实力。',
    riskArea: '整体平稳，保持警觉，定期复盘。',
    proactive: '适合稳健推进既有计划，不急不躁。',
    career: domainDefault,
    wealth: domainDefault,
    love: domainDefault,
    health: domainDefault,
    keyAdvice: ['运势平稳，脚踏实地积累，等待更有利的流年窗口。'],
    auspicious: [`幸运颜色：绿色`, `吉利方位：东方`],
    resolve: undefined,
    isCurrent: true,
    factors: [],
  };
}

export function getCurrentLiunian(
  fourPillars: FourPillars,
  birthYear: number,
  birthMonth: number,
  birthDay: number,
  gender: 'male' | 'female',
  currentYear: number,
): LiunianItem {
  // 若出生年晚于当前年（边界情况），直接返回基于当年的兜底对象
  if (currentYear < birthYear) {
    return buildFallbackLiunian(currentYear, fourPillars);
  }
  const list = calcLiunianRange(fourPillars, birthYear, birthMonth, birthDay, gender, currentYear, { past: 0, future: 0 });
  return list[0] ?? buildFallbackLiunian(currentYear, fourPillars);
}

export function getLiunianLevelColor(level: LiunianItem['overallLevel']): string {
  const map: Record<LiunianItem['overallLevel'], string> = {
    '大吉': '#E8A020',
    '吉': '#7BC67E',
    '平顺': '#4A90D9',
    '需注意': '#B79AC5',
    '挑战': '#D14B45',
  };
  return map[level];
}

export { ELEMENT_COLORS };